<script setup>
import dayjs from 'dayjs'
import { computed, onMounted, ref, watch } from 'vue'
import { getI18n } from '@/plugins/i18n'
import { DxDataGrid } from 'devextreme-vue/data-grid'
import CustomStore from 'devextreme/data/custom_store'

import { exchangeRateSourceEnum, infoBoxIdEnum, infoBoxToneEnum, usePortalStore } from '@/stores/portal.store'
import { useWebSiteStore } from '@/stores/web-site.store'

import * as dataGrid from '@/composables/data-grid'
import { useDetectDevice } from '@/composables/device'
import { useNotify } from '@/composables/notify'
import { useHighlightNewRow } from '@/composables/scroll'
import { currencyCode, exchangeRateSource, formatExchangeRate } from '@/composables/exchange-rate'
import { formatShortDate } from '@/lib/utils'

import ChipTemplate from '@/components/shared/ChipTemplate.vue'
import ConfirmDialog from '@/components/shared/ConfirmDialog.vue'
import ListItem from '@/components/shared/ListItem.vue'
import ExchangeRateEcbStatus from '@/components/accounting/ExchangeRateEcbStatus.vue'
import CurrencySelect from '@/components/shared/CurrencySelect.vue'
import ExchangeRateEdit from '@/components/accounting/ExchangeRateEdit.vue'
import InfoBox from '@/components/shared/InfoBox.vue'

const props = defineProps({
  referenceCurrencyRef: {
    type: Number,
    default: null,
  },
  savedReferenceCurrencyRef: {
    type: Number,
    default: null,
  },
  printExchangeRate: {
    type: Boolean,
    default: true,
  },
})

const emit = defineEmits(['update:referenceCurrencyRef', 'update:printExchangeRate'])

const i18n = getI18n()
const portalStore = usePortalStore()
const webSiteStore = useWebSiteStore()
const { isMobile } = useDetectDevice()
const { notifySuccess } = useNotify()
const { gridRef } = dataGrid.useGridRef()
const { highlightedRowId, highlightRowAsync } = useHighlightNewRow()
const ratesListRef = ref(null)
const editItem = ref(null)
const editMode = ref(false)
const rowToDelete = ref(null)
const showDeleteDialog = ref(false)

const rateHeader = computed(() => `1 ${currencyCode(props.savedReferenceCurrencyRef)} =`)
const isReferenceChanged = computed(() => props.referenceCurrencyRef !== props.savedReferenceCurrencyRef)

const foreignCurrencies = computed(() => webSiteStore.getLocalizedCurrencies()
  .filter(c => c.id !== props.savedReferenceCurrencyRef)
  .sort((a, b) => a.code.localeCompare(b.code)))
const addableCurrencies = computed(() => foreignCurrencies.value.filter(c => c.manual_exchange_rate == null))
const ecbCurrencies = computed(() => foreignCurrencies.value.filter(c => c.ecb_exchange_rate != null))
const latestEcbDate = computed(() => ecbCurrencies.value.map(c => c.ecb_exchange_rate_date).sort().at(-1))
const ecbUpdatedAt = computed(() => ecbCurrencies.value[0]?.ecb_exchange_rate_updated_at)

const manualRowKey = (currencyRef) => `${exchangeRateSourceEnum.MANUAL}-${currencyRef}`

const rows = computed(() => foreignCurrencies.value.flatMap((currency) => {
  const hasManualRate = currency.manual_exchange_rate != null
  return [
    hasManualRate && {
      key: manualRowKey(currency.id),
      currency_ref: currency.id,
      currency_code: currency.code,
      currency_name: currency.name,
      currency_symbol: currency.symbol || currency.code,
      currency_label: `${currency.code} - ${currency.name}`,
      source: exchangeRateSourceEnum.MANUAL,
      exchange_rate: Number(currency.manual_exchange_rate),
      exchange_rate_date: currency.manual_exchange_rate_date,
      updated_at: currency.manual_exchange_rate_updated_at,
    },
    currency.ecb_exchange_rate != null && {
      key: `${currency.ecb_exchange_rate_source}-${currency.id}`,
      currency_ref: currency.id,
      currency_code: currency.code,
      currency_name: currency.name,
      currency_symbol: currency.symbol || currency.code,
      currency_label: `${currency.code} - ${currency.name}`,
      source: currency.ecb_exchange_rate_source,
      exchange_rate: Number(currency.ecb_exchange_rate),
      exchange_rate_date: currency.ecb_exchange_rate_date,
      overridden: hasManualRate,
    },
  ].filter(Boolean)
}))

const isManualRow = (row) => row?.source === exchangeRateSourceEnum.MANUAL

const store = new CustomStore({
  key: 'key',
  loadMode: 'raw',
  cacheRawData: false,
  load: async() => {
    await webSiteStore.fetchCurrenciesAsync({ all: true }, true)
    return rows.value
  },
  remove: (key) => webSiteStore.updateCurrencyAsync(rows.value.find(row => row.key === key).currency_ref, { exchange_rate: null, exchange_rate_date: null }),
})

const isNewRate = ref(false)
const editCurrencies = computed(() => (isNewRate.value ? addableCurrencies.value : foreignCurrencies.value))

const openEdit = (row) => {
  isNewRate.value = !isManualRow(row)
  editItem.value = {
    currency_ref: row?.currency_ref ?? null,
    exchange_rate: row?.exchange_rate ?? null,
    exchange_rate_date: isNewRate.value ? dayjs().format('YYYY-MM-DD') : row.exchange_rate_date,
  }
}

const fetchCurrenciesAsync = () => webSiteStore.fetchCurrenciesAsync({ all: true }, true)

const reloadAsync = async(createdRowKey = null) => {
  if (!isMobile.value) {
    await dataGrid.refreshGridAsync(gridRef, createdRowKey)
    return
  }
  await fetchCurrenciesAsync()
  await highlightRowAsync(createdRowKey, ratesListRef)
}

const onEditSaved = async(saved) => {
  editItem.value = null
  await reloadAsync(isNewRate.value && saved ? manualRowKey(saved.currency_ref) : null)
}

const onRowClick = (row) => {
  if (editMode.value && !row.overridden) openEdit(row)
}

const onDelete = (row) => {
  rowToDelete.value = row
  showDeleteDialog.value = true
}

const onDeleteConfirmed = async() => {
  await store.remove(rowToDelete.value.key)
  notifySuccess(i18n.t('info.deletedSuccessfully'))
  await reloadAsync()
}

const columns = [
  {
    type: 'buttons',
    showInColumnChooser: false,
    buttons: [
      { name: 'edit', onClick: (e) => openEdit(e.row.data) },
      { name: 'delete' },
    ],
    fixed: false,
    visibleIndex: -1,
    width: '6rem',
  },
  {
    dataField: 'currency_label',
    caption: i18n.t('common.currency'),
    width: '16rem',
    sortOrder: 'asc',
  },
  {
    dataField: 'source',
    caption: i18n.t('company.exchangeRates.source'),
    width: '9rem',
    alignment: 'center',
    lookup: { dataSource: portalStore.getLocalizedExchangeRateSources(), valueExpr: 'id', displayExpr: 'name' },
    cellTemplate: 'source-cell-template',
  },
  {
    dataField: 'exchange_rate',
    caption: rateHeader.value,
    dataType: 'number',
    width: '9rem',
    format: '#,##0.######',
  },
  {
    dataField: 'exchange_rate_date',
    caption: i18n.t('company.exchangeRates.rateDate'),
    dataType: 'date',
    width: '9rem',
  },
  {
    dataField: 'updated_at',
    caption: i18n.t('company.exchangeRates.lastModified'),
    dataType: 'datetime',
    width: '11rem',
  },
]

const gridOptions = dataGrid.useDefaultGridOptions({
  dataSource: store,
  columns,
  isDetailGrid: true,
  height: '60vh',
  remoteOperations: false,
  paging: { enabled: false },
  sorting: { mode: 'multiple' },
  editing: dataGrid.useDefaultEditing(null, null, {
    allowAdding: false,
    allowUpdating: ({ row }) => !row.data.overridden,
    allowDeleting: ({ row }) => isManualRow(row.data),
  }),
  addButton: { onClick: () => openEdit(null) },
  beforeToolbarItems: [{ location: 'before', template: 'ecb-status' }],
  onRowPrepared: (e) => {
    if (e.rowType !== 'data' || !e.data.overridden) return
    e.rowElement.classList.add('text-grey-6')
    e.rowElement.title = i18n.t('company.exchangeRates.overriddenByManual')
  },
})

onMounted(() => { if (isMobile.value) fetchCurrenciesAsync() })

watch(() => props.savedReferenceCurrencyRef, () => reloadAsync())
watch(rateHeader, (caption) => gridRef.value?.instance?.columnOption('exchange_rate', 'caption', caption))
</script>
<template>
  <div>
    <q-card-section class="q-pt-none">
      <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
        <q-icon
          name="mdi-cog-outline"
          class="q-mr-sm"
          color="primary"
          size="sm"
        />
        <span class="dialog-card-section-title">
          {{ i18n.t('company.exchangeRates.settings') }}
        </span>
      </q-item-label>
      <div class="q-ml-sm q-pl-md row items-center q-col-gutter-md">
        <div
          class="row items-center no-wrap"
          :class="{ 'col-11': isMobile }"
          :style="isMobile ? undefined : 'width: 22rem;'"
        >
          <currency-select
            :model-value="props.referenceCurrencyRef"
            :clearable="false"
            :label="i18n.t('company.exchangeRates.referenceCurrency')"
            class="col"
            @update:model-value="(value) => emit('update:referenceCurrencyRef', value)"
          />
          <info-box popup class="q-ml-xs" :message="i18n.t('company.exchangeRates.referenceCurrencyHint')" />
        </div>
        <div class="row items-center no-wrap">
          <q-toggle
            :model-value="props.printExchangeRate"
            :label="i18n.t('company.exchangeRates.printExchangeRate')"
            @update:model-value="(value) => emit('update:printExchangeRate', value)"
          />
          <info-box popup class="q-ml-xs" :message="i18n.t('company.exchangeRates.printExchangeRateHint')" />
        </div>
      </div>
      <info-box
        v-if="isReferenceChanged"
        class="q-ml-sm q-pl-md q-mt-md"
        :tone="infoBoxToneEnum.WARNING"
        :message="`${i18n.t('company.exchangeRates.rebaseOnSave')} ${i18n.t('company.exchangeRates.saveReferenceFirst')}.`"
      />
    </q-card-section>

    <q-card-section class="q-pt-none">
      <q-item-label header class="q-px-none" style="display: flex; align-items: center;">
        <q-icon
          name="mdi-swap-horizontal"
          class="q-mr-sm"
          color="primary"
          size="sm"
        />
        <span class="dialog-card-section-title">
          {{ i18n.t('common.exchangeRate', 2) }}
        </span>
      </q-item-label>
      <div :class="{ 'q-ml-sm q-pl-md': !isMobile }">
        <q-dialog
          :model-value="editItem !== null"
          class="dialog-xs"
          :maximized="isMobile"
          @hide="editItem = null"
        >
          <exchange-rate-edit
            v-if="editItem !== null"
            :model-value="editItem"
            :currencies="editCurrencies"
            :rate-label="rateHeader"
            :is-new="isNewRate"
            @update="onEditSaved"
            @cancel="editItem = null"
          />
        </q-dialog>
        <div v-if="isMobile" :class="{ disabled: isReferenceChanged }">
          <exchange-rate-ecb-status class="q-px-md q-pb-sm" :updated-at="ecbUpdatedAt" :rates-date="latestEcbDate" />
          <div class="row justify-end items-center q-px-md q-pb-sm">
            <q-btn
              v-if="rows.length"
              class="q-mr-sm"
              flat
              round
              dense
              :icon="editMode ? 'mdi-pencil-off' : 'mdi-pencil'"
              :color="editMode ? 'primary' : 'grey-6'"
              @click="editMode = !editMode"
            >
              <q-tooltip>{{ editMode ? i18n.t('common.done') : i18n.t('common.edit') }}</q-tooltip>
            </q-btn>
            <q-btn
              style="padding: 0;"
              size="md"
              unelevated
              ripple
              rounded
              icon="mdi-plus-circle-outline"
              @click="openEdit(null)"
            />
          </div>
          <q-list v-if="rows.length" ref="ratesListRef">
            <q-item
              v-for="row in rows"
              :key="row.key"
              class="q-py-sm q-pl-sm q-pr-md"
              :class="{ 'row-highlight': row.key === highlightedRowId }"
              :clickable="editMode && !row.overridden"
              @click="onRowClick(row)"
            >
              <list-item
                :avatar-icon="isManualRow(row) ? 'mdi-account-edit-outline' : 'mdi-bank-outline'"
                avatar-icon-color="primary"
                :label="row.currency_symbol"
                :label-suffix="row.currency_name"
                :caption-chips="[exchangeRateSource(row.source)]"
                :disabled="row.overridden"
                dense
              >
                <template #right>
                  <div class="row items-center no-wrap q-gutter-x-sm">
                    <div class="column items-end">
                      <q-item-label class="text-no-wrap">{{ rateHeader }} {{ formatExchangeRate(row.exchange_rate) }} {{ row.currency_code }}</q-item-label>
                      <q-item-label caption>{{ formatShortDate(row.exchange_rate_date) }}</q-item-label>
                    </div>
                    <template v-if="editMode">
                      <q-btn
                        :class="{ invisible: row.overridden }"
                        style="padding: 0;"
                        size="md"
                        flat
                        rounded
                        color="primary"
                        icon="mdi-pencil-circle-outline"
                        :disable="row.overridden"
                        @click.stop="openEdit(row)"
                      />
                      <q-btn
                        :class="{ invisible: !isManualRow(row) }"
                        style="padding: 0;"
                        size="md"
                        flat
                        rounded
                        color="negative"
                        icon="mdi-minus-circle-outline"
                        :disable="!isManualRow(row)"
                        @click.stop="onDelete(row)"
                      />
                    </template>
                  </div>
                </template>
              </list-item>
            </q-item>
          </q-list>
          <div v-else class="text-center text-grey-6 q-pa-lg">
            {{ i18n.t('common.noData') }}
          </div>
          <confirm-dialog
            v-model="showDeleteDialog"
            :message="i18n.t('common.confirmDelete')"
            icon="mdi-delete-outline"
            icon-color="negative"
            @confirm="onDeleteConfirmed"
          />
        </div>
        <dx-data-grid
          v-else
          ref="gridRef"
          v-bind="gridOptions"
          :class="{ disabled: isReferenceChanged }"
        >
          <template #ecb-status>
            <exchange-rate-ecb-status :updated-at="ecbUpdatedAt" :rates-date="latestEcbDate" />
          </template>
          <template #source-cell-template="{ data: templateOptions }">
            <chip-template :values="[exchangeRateSource(templateOptions.data.source)]" justify="center" />
          </template>
        </dx-data-grid>
        <info-box :id="infoBoxIdEnum.ECB_DISCLAIMER" :class="isMobile ? 'q-ma-md' : 'q-mt-md'" :message="i18n.t('company.exchangeRates.disclaimer')" />
      </div>
    </q-card-section>
  </div>
</template>
